import React from "react";
import { BlockMath, InlineMath } from "react-katex";
import { sample } from "lodash";
import RootFractionInput, {
  parseRootFractionInput,
  formatLatexPreview,
} from "../../../../components/inputs/RootFractionInput";
import addFractionsWithSqrt from "../../../../utils/AddFractionsWithSqrt"; // dostosuj ścieżkę jeśli inna

const VALUES = {
  sin: {
    0: "0",
    30: "\\dfrac{1}{2}",
    45: "\\dfrac{\\sqrt{2}}{2}",
    60: "\\dfrac{\\sqrt{3}}{2}",
    90: "1",
  },
  cos: {
    0: "1",
    30: "\\dfrac{\\sqrt{3}}{2}",
    45: "\\dfrac{\\sqrt{2}}{2}",
    60: "\\dfrac{1}{2}",
    90: "0",
  },
  tan: {
    0: "0",
    30: "\\dfrac{\\sqrt{3}}{3}",
    45: "1",
    60: "\\sqrt{3}",
    90: "nie istnieje",
  },
};

function parseLatexFrac(latex) {
  const fracMatch = latex.match(/^\\dfrac{(.+)}{(.+)}$/);
  if (fracMatch) {
    const numerator = fracMatch[1].replace(/\\\\/g, "\\"); // naprawia podwójne \\
    const denominator = parseFloat(fracMatch[2]);
    return { numerator, denominator };
  }

  // np. \sqrt{3} → √3
  if (latex.startsWith("\\sqrt")) {
    const cleaned = latex.replace(/\\sqrt{(.+)}/, "√$1");
    return { numerator: cleaned, denominator: 1 };
  }

  if (latex === "0") return { numerator: "0", denominator: 1 };

  return { numerator: latex, denominator: 1 };
}

export function generateTask() {
  let func1, angle1, func2, angle2, val1, val2;

  do {
    func1 = sample(["sin", "cos", "tan"]);
    angle1 = sample([0, 30, 45, 60, 90]);
    val1 = VALUES[func1][angle1];

    func2 = sample(["sin", "cos", "tan"]);
    angle2 = sample([0, 30, 45, 60, 90]);
    val2 = VALUES[func2][angle2];
  } while (
    val1 === "nie istnieje" ||
    val2 === "nie istnieje"
  );

  const operator = sample(["+", "-"]);

  const left = parseLatexFrac(val1);
  const right = parseLatexFrac(val2);
  const { latex, value } = addFractionsWithSqrt({
    left,
    right,
    operation: operator,
  });

const task = {
  type: "AddOrSubtractTrigFunctionsTask",
  func1,
  angle1,
  func2,
  angle2,
  operator,
  value1: val1,
  value2: val2,
  expectedLatex: `${val1} ${operator} ${val2} = ${latex}`,
  expectedAnswer: value.toString(),
};

return {
  ...task,
  data: task, // ⬅️ KLUCZOWE – potrzebne, by renderExplanation działał w quizie
};

}

export function renderTask({ func1, angle1, func2, angle2, operator }) {
  return (
    <p>
      Oblicz wartość wyrażenia:{" "}
      <InlineMath
        math={`\\${func1}(${angle1}^\\circ) ${operator} \\${func2}(${angle2}^\\circ)`}
      />
    </p>
  );
}

export function renderInput(task, userAnswer, onChange, disabled) {
  return (
    <RootFractionInput
      value={userAnswer}
      onChange={onChange}
      disabled={disabled}
      label=""
    />
  );
}

export function renderUserAnswer(userAnswer) {
  const raw = typeof userAnswer === "object" ? userAnswer?.raw : userAnswer;
  if (!raw || raw.trim() === "") return <i>(brak odpowiedzi)</i>;
  return <InlineMath math={formatLatexPreview(raw)} />;
}

export function checkAnswer(task, userAnswer) {
  const normalize = (str) =>
    String(str)
      .replace(/\s/g, "")
      .replace(/\*/g, "")
      .replace(/sqrt\(([^)]+)\)/g, "√$1")
      .replace(/\\sqrt{([^}]+)}/g, "√$1")
      .replace(/\\dfrac{([^}]+)}{([^}]+)}/g, "$1/$2")
      .replace(/\\frac{([^}]+)}{([^}]+)}/g, "$1/$2")
      .replace(/[()]/g, "")
      .replace(/\\/g, "")
      .toLowerCase();

  const raw = typeof userAnswer === "object" ? userAnswer.raw : userAnswer;
  const parsed = parseRootFractionInput(raw);
  const expected = parseFloat(task.expectedAnswer);

  const approxEqual =
    parsed != null && Math.abs(parsed - expected) < 0.01;

  return {
    isCorrect: approxEqual,
    comment: approxEqual
      ? null
      : `Oczekiwano: ${formatLatexPreview(task.expectedAnswer)}`,
  };
}

export function renderAnswer({ expectedLatex }) {
  const expr = expectedLatex.split("=").at(-1).trim();
  return (
    <div style={{ marginTop: "0.5rem" }}>
      <InlineMath math={expr} />
    </div>
  );
}

export function renderExplanation(input) {
  const data = input?.data ?? input;
  const { func1, angle1, func2, angle2, value1, value2, expectedLatex } = data;

  return (
    <div className="space-y-4">
      <div>
        <strong>Krok 1:</strong> Odczytujemy wartości:
        <BlockMath math={`\\${func1}(${angle1}^\\circ) = ${value1}`} />
        <BlockMath math={`\\${func2}(${angle2}^\\circ) = ${value2}`} />
      </div>
      <div>
        <strong>Krok 2:</strong> Podstawiamy i obliczamy:
        <BlockMath math={expectedLatex} />
      </div>
    </div>
  );
}
